import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";

export type MarketplaceCompanySearchJSON = z.infer<typeof MarketplaceCompanySearchSchema.MarketplaceCompanySearchZ>;

export default class MarketplaceCompanySearchSchema {
  public static readonly MarketplaceCompanySearchZ = z.object({
    company: z.string().optional(),
    query: z.string().optional(),
    name: z.string().optional(),
    yearEstablished: z.number().nullish(),
    numberOfEmployees: z.number().nullish(),
    contactPersonUserId: z.string().optional(),
    description: z.string().optional(),
    address1: z.string().optional(),
    address2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
    website: z.string().optional(),
    from: z.number().default(0),
    size: z.number().default(10),
  });

  public static validateJSON(json: unknown): MarketplaceCompanySearchJSON {
    return parseOrThrow(MarketplaceCompanySearchSchema.MarketplaceCompanySearchZ, json, {
      logError: true,
      schemaName: "MarketplaceCompanySearchSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValidJSON(json: unknown): json is MarketplaceCompanySearchJSON {
    return MarketplaceCompanySearchSchema.MarketplaceCompanySearchZ.safeParse(json).success;
  }

  public static deserialize(values?: Partial<MarketplaceCompanySearchJSON>): MarketplaceCompanySearchJSON {
    return MarketplaceCompanySearchSchema.validateJSON(values ?? {});
  }

  public static toSearchParams(search: MarketplaceCompanySearchJSON): URLSearchParams {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(search)) {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    }
    return params;
  }

  public static toCacheKey(search: MarketplaceCompanySearchJSON): string {
    const params = MarketplaceCompanySearchSchema.toSearchParams(search);
    const sortedEntries = [...params.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    return sortedEntries.map(([k, v]) => `${k}=${v}`).join("&");
  }
}
